<template>
    <confirmation-dialog
        :show="show && !loading"
        title=""
        :show-icon="false"
        :proceed-button-text="nextButtonText"
        @proceed="gotoNextSection()"
        :proceed-disabled="form.busy || creating || disableNextButton"
        :show-cancel-button="(!form.busy || creating) && !disableCloseButton"
        :show-proceed-button="!disableCloseButton"
        :cancel-button-text="previousButtonText"
        proceed-button-colour="indigo-600"
        proceed-button-hover-colour="indigo-800"
        @cancelled="gotoPreviousSection()">
        <!-- Show other modal pieces based on current step used -->
        <div slot="title" class="flex flex-col space-y-4 mb-4">
            <div class="flex justify-between items-center">
                <div class="flex items-center space-x-4">
                    <icon-tag class="h-10 w-10 text-white bg-indigo-600 p-1 rounded"></icon-tag>

                    <div class="text-indigo-600 font-bold text-lg">
                        <span v-if="isCurrentSection(1)">Fill In Ticket Details</span>
                        <span v-else-if="isCurrentSection(2)">{{ inspection ? "Verify Selected Checklist Items" : "Create & Edit Issues" }}</span>
                        <span v-else-if="isCurrentSection(3)">Verify Service Provider Task Settings</span>
                        <span v-else-if="isCurrentSection(4)">Select Communication</span>
                        <span v-else-if="isCurrentSection(5)">Finalize Ticket</span>
                    </div>

                    <portal-target name="titleDropdownPortal" v-show="isCurrentSection(4)"></portal-target>
                </div>

                <button class="text-md text-gray-400 hover:text-indigo-600 transition duration-300 p-1" v-show="!disableCloseButton"
                        @click="gotoPreviousSection(true)" :disabled="form.busy || creating || disableCloseButton">x
                </button>
            </div>

            <simple-steps v-if="show" :total-steps="totalModalSections" :current-step="currentModalSection"></simple-steps>
        </div>

        <ticket-create-modal-select-details
            style="min-height: 29rem; max-height: 29rem"
            v-show="isCurrentSection(1)"
            :form="form"
            :reporting-methods="reporterMethods"
            :asset="selectedAsset"
            :contact-group="selectedContactGroup"
            @asset-changed="setSelectedAsset"
            @contact-group-changed="setSelectedContactGroup"
            :tenant="selectedTenant"
            @tenant-changed="setSelectedTenant"
            @landlord-changed="setSelectedLandlord"
            :user="assignedToUser"
            @assigned-changed="setAssignedUser"
            slot="body">
        </ticket-create-modal-select-details>

        <ticket-create-modal-create-issues
            style="min-height: 29rem; max-height: 29rem"
            ref="ticketCreateIssuesSection"
            v-show="isCurrentSection(2)"
            :form="form"
            :inspection="inspection"
            :maintenance-request="maintenanceRequest"
            :gotFocus="currentModalSection === 2"
            @disableNextButton="(disable) => disableNextButton = disable"
            slot="body">
        </ticket-create-modal-create-issues>

        <task-service-provider-modal-select-task-settings
            style="min-height: 29rem; max-height: 29rem"
            v-show="isCurrentSection(3)"
            :form="form"
            slot="body">
        </task-service-provider-modal-select-task-settings>

        <task-service-provider-modal-communication
            ref="communicationSection"
            :selected-tenant="selectedTenant"
            :selected-landlord="selectedLandlord"
            :communication-scenarios="communicationScenarios"
            :maintenance-request-id="maintenanceRequest ? maintenanceRequest.id : null"
            :gotFocus="currentModalSection === 4"
            @disableNextButton="(disable) => disableNextButton = disable"
            @toggle-close-button="toggleCloseButton()"
            style="min-height: 29rem; max-height: 29rem"
            v-show="isCurrentSection(4)"
            :form="form"
            slot="body">
        </task-service-provider-modal-communication>

        <ticket-create-modal-summary
            style="min-height: 29rem; max-height: 29rem"
            v-show="isCurrentSection(5)"
            :form="form"
            :ticket-phases="ticketPhases"
            slot="body">
        </ticket-create-modal-summary>
    </confirmation-dialog>
</template>

<script>
import MultiSectionModal from "../../../mixins/multi-section-modal";
import ContactCommunicationMixin from "../../../mixins/contact-communication-mixin";
import dayjs from "dayjs";

export default {
    name: "TicketCreateModalBase",

    mixins: [MultiSectionModal, ContactCommunicationMixin],

    props: {
        show: {
            required: true,
            type: Boolean,
        },
        maintenanceRequest: {
            required: false,
            type: Object,
            default: null,
            description: "Used when creating a ticket from a Maintenance Request"
        },
        selectedIssues: {
            required: false,
            type: Array,
            description: "Used when creating a ticket from a Maintenance Request"
        },
        inspection: {
            required: false,
            type: Object,
            default: null,
            description: "Used when creating a ticket from an Inspection"
        },
        selectedChecklistItems: {
            required: false,
            type: Array,
            description: "Used when creating a ticket from a Inspection"
        },
        asset: {
            required: false,
            type: Object,
            default: null,
            description: "Used when creating a ticket from an asset's dashboard or inspection"
        },
        threadId: {
            required: false,
            type: Number,
            default: null,
            description: "Used when creating a ticket from an email"
        },
    },

    data () {
        return {
            loading: true,
            creating: false,
            assignedToUser: null,
            selectedAsset: null,
            selectedTenant: null,
            selectedContactGroup: null,
            selectedLandlord: null,
            reporterMethods: [],
            defaultReminderDays: {},
            ticketPhases: [],
            disableCloseButton: false,

            communicationScenarios: [],
            form: new AppForm({
                ticket_details: {},
                todo: {},
                ticket_notification_settings: {},
                issues: {},
                task_settings: {},
                service_providers: [],
                communication_scenario_type: null,
                errors: {},
            }),
        };
    },

    mounted () {
        this.finalButtonText = "Create";
        this.totalModalSections = 5;
        this.assignedToUser = this.state.user;

        if (this.maintenanceRequest) {
            this.maintenanceRequestChanged();
        }

        let self = this;
        Bus.$on("final-click", function () {
            self.createTicket();
        });
    },

    beforeDestroy () {
        // Turn off event listener to prevent multiple instances listening and
        // sending the create ticket request multiple times
        Bus.$off("final-click");
    },

    watch: {
        show (val) {
            // only reset the form if there has been no options chosen
            if (val && this.form && (this.inspection || _.isEmpty(this.form.ticket_details))) {
                this.setAssignedUser(this.state.user);
                this.refreshModal();
            }
        },

        // Refresh entire form when creating ticket from different maintenance requests
        maintenanceRequest: {
            handler: function () {
                this.maintenanceRequestChanged();
            },
            deep: true
        },

        // Refresh entire form when creating ticket from different emails
        threadId () {
            this.refreshModal();
        },

        selectedIssues () {
            this.form.issues = this.setupIssues();
        },

        selectedChecklistItems () {
            this.form.issues = this.setupIssues();
        },

        creating (newVal) {
            this.finalButtonText = newVal ? "Working..." : "Create";
            this.setNextButtonText();
        },

        currentModalSection (section) {
            if (section === 3) {
                this.$refs.ticketCreateIssuesSection.autoFinalizeIssueWhenClickingNext();
            }
        },

        "form.todo.reminder_date": function (newDate) {
            if (!newDate) {
                this.form.todo.users.splice(0);
            } else if (this.form.todo.users && this.form.todo.users.length === 0) {
                this.form.todo.users.push({id: this.state.user.id, name: this.state.user.name});
            }
        },

        "form.ticket_details.priority": function (priority) {
            let interval = 0;
            switch (priority) {
                case ("Low"):
                    interval = this.defaultReminderDays.low;
                    break;
                case ("Medium"):
                    interval = this.defaultReminderDays.medium;
                    break;
                case ("High"):
                    interval = this.defaultReminderDays.high;
                    break;
            }

            this.$set(this.form.todo, "reminder_date", this.$dayjs().add(interval, "days").format("YYYY-MM-DD"));
        },
    },

    methods: {
        refreshModal () {
            this.setupModalAndForm()
            .then(() => this.getCommunicationScenarios()
            .then(() => this.loading = false
            ));
        },

        setupModalAndForm () {
            return new Promise((resolve, reject) => {
                this.loading = true;

                // Reset
                this.setSelectedAsset(this.asset);
                this.setSelectedTenant(this.resolvePrimaryTenant());
                this.setSelectedLandlord(this.resolvePrimaryLandlord());
                this.setSelectedContactGroup(this.resolveContactGroup());
                this.currentModalSection = 1;

                axios.get("/settings/tickets/show")
                .then((response) => {
                    this.reporterMethods = response.data.report_methods;
                    this.defaultReminderDays = {
                        low: response.data.settings.reminder_in_days_low,
                        medium: response.data.settings.reminder_in_days_medium,
                        high: response.data.settings.reminder_in_days_high
                    };
                    this.ticketPhases = response.data.phases.map(function (phase) {
                        return {id: phase.id, phase: phase.phase};
                    });

                    // setup ticket issues from maintenance request or inspection
                    let issues = this.setupIssues();

                    if (this.maintenanceRequest) {
                        if (this.maintenanceRequest.asset?.id) {
                            this.selectedAsset = this.maintenanceRequest.asset;
                        }

                        if (this.maintenanceRequest.contact?.id) {
                            this.tenants = [this.maintenanceRequest.contact];
                        }
                    }

                    let reportMethod;
                    let reporter = "Tenant";
                    switch (true) {
                        case (this.maintenanceRequest !== null):
                            reportMethod = "RedRabbit Online";
                            break;
                        case (this.inspection !== null):
                            reportMethod = "Inspection";
                            reporter = "Inspector";
                            break;
                        case (this.threadId !== null):
                            reportMethod = "Email";
                            break;
                        default:
                            reportMethod = "Phone Call";
                            break;
                    }

                    // Setup Form
                    this.form = new AppForm({
                        ticket_details: {
                            asset_id: this.selectedAsset ? this.selectedAsset.id : null,
                            portfolio_id: this.selectedAsset ? this.selectedAsset.portfolio_id : null,
                            tenant_id: (this.selectedTenant && !this.selectedContactGroup) ? this.selectedTenant.id : null,
                            landlord_id: this.selectedLandlord?.id,
                            contact_group_id: this.selectedContactGroup ? this.selectedContactGroup.id : null,
                            assigned_to: this.state.user.id,
                            priority: "Medium",
                            phase_id: Object.entries(this.ticketPhases)[0][1].id,
                            reporter: reporter,
                            report_method: reportMethod,
                            description: "",
                            auto_generated_description: true,
                            maintenance_request_id: this.maintenanceRequest ? this.maintenanceRequest.id : null,
                            inspection_id: this.inspection ? this.inspection.id : null,
                            thread_id: this.threadId ? this.threadId : null,
                            logged_by: this.state.user.id,
                        },
                        todo: {
                            reminder_date: this.$dayjs().add(this.defaultReminderDays.medium, "days").format("YYYY-MM-DD"),
                            users: [{id: this.state.user.id, name: this.state.user.name}],
                        },
                        ticket_notification_settings: {},
                        task_settings: {
                            show_landlord_details: response.data.settings.task_card_include_landlord_details,
                            show_tenant_details: response.data.settings.task_card_include_tenant_details,
                            requires_quality_assurance_sign_off: response.data.settings.task_card_requires_quality_assurance_sign_off,
                            generate_pdf: response.data.settings.task_card_generate_pdf,
                            include_photos: response.data.settings.task_card_include_issue_photos,
                            include_signatures: response.data.settings.task_card_include_signature_lines,
                            instruction_message: "",
                        },
                        service_providers: [], //id, communication_channels: {type, message}
                        issues: issues,
                    });

                    resolve();
                })
                .catch((error) => {
                        toastr.error(error.message);
                        reject();
                    }
                );
            });
        },

        getCommunicationScenarios () {
            return new Promise((resolve, reject) => {
                this.loading = true;
                axios.get("/communication-scenarios/search", {
                    params: {
                        scenario: ["opening ticket", "opening ticket from maintenance request", "requesting quote", "tasking service provider"]
                    }
                })
                .then(response => {
                    this.communicationScenarios = response.data;
                    resolve();
                })
                .catch(error => {
                        toastr.error(error.message);
                        reject();
                    }
                );
            });
        },

        resolvePrimaryTenant () {
            if (this.maintenanceRequest?.contact?.id) {
                return this.maintenanceRequest.contact;
            }

            if (this.inspection?.tenant?.id) {
                return this.inspection.tenant;
            }

            if (this.selectedAsset?.tenant) {
                return this.selectedAsset.tenant;
            }

            if (this.selectedAsset?.tenants) {
                return this.selectedAsset.tenants.filter(tenant => tenant.pivot.primary == true)?.[0];
            }

            if (this.selectedAsset?.contacts) {
                return this.selectedAsset.contacts.filter(contact => contact.pivot.primary == true && contact.pivot.role === "tenant")?.[0];
            }

            return null;
        },

        resolvePrimaryLandlord () {
            if (this.inspection?.landlord?.id) {
                return this.inspection.landlord;
            }

            if (this.selectedAsset?.landlord) {
                return this.selectedAsset.landlord;
            }

            if (this.selectedAsset?.landlords) {
                return this.selectedAsset.landlords.filter(landlord => landlord.pivot.primary == true)?.[0];
            }

            if (this.selectedAsset?.contacts) {
                return this.selectedAsset.contacts.filter(contact => contact.pivot.primary == true && contact.pivot.role === "landlord")?.[0];
            }

            return null;
        },

        resolveContactGroup () {
            if (this.inspection?.lease_contact_group?.id) {
                return this.inspection.lease_contact_group;
            }

            return null;
        },

        setupIssues () {
            if (this.form) {
                this.$set(this.form, "issues", []);
            }
            let issues = [];

            // Setup issues from maintenance request
            if (this.maintenanceRequest && this.selectedIssues && this.selectedIssues.length > 0) {
                this.selectedIssues.forEach((issue, index) => {
                    // use this trick to create a temp object for deep cloning and not just cloning with reference
                    let temp = JSON.parse(JSON.stringify({
                        issueIndex: index,
                        maintenance_request_issue_id: issue.id,
                        item: issue.item,
                        location: issue.location,
                        description: issue.description,
                        required_action: "Quote",
                        files: issue.media,
                    }));
                    issues.push(temp);
                });
            }

            // Setup issues from Inspection
            if (this.inspection && this.selectedChecklistItems && this.selectedChecklistItems.length > 0) {
                this.selectedChecklistItems.forEach((item, index) => {

                    //Generate issue description from checklist item details
                    let tempDescription = "";

                    let condition = [];
                    item.cleanliness ? condition.push(item.cleanliness) : "";
                    item.damage ? condition.push(item.damage) : "";
                    item.functioning ? condition.push(item.functioning) : "";

                    if (condition.length > 0) {
                        tempDescription += condition.join(", ") + ". ";
                    }

                    item.comments ? tempDescription += item.comments : "";

                    item.recommendation ? tempDescription += " (" + item.recommendation + ")" : "";

                    // use this trick to create a temp object for deep cloning and not just cloning with reference
                    let temp = JSON.parse(JSON.stringify({
                        issueIndex: index,
                        checklist_item_id: item.id,
                        item: item.item,
                        location: item.area_name,
                        description: tempDescription,
                        required_action: "Quote",
                        photo: item.photo,
                    }));
                    issues.push(temp);
                });
            }

            return issues;
        },

        setSelectedAsset (asset) {
            this.selectedAsset = asset;
            this.form.ticket_details.asset_id = this.selectedAsset ? this.selectedAsset.id : null;
            this.form.ticket_details.portfolio_id = this.selectedAsset ? this.selectedAsset.portfolio_id : null;

            if (!this.selectedAsset) {
                this.setSelectedTenant(null);
                this.setSelectedLandlord(null);
                this.setSelectedContactGroup(null);
            } else {
                if (this.form.errors.has("ticket_details.asset_id")) {
                    this.form.errors.forget("ticket_details.asset_id");
                }
            }

            let self = this;
            this.$nextTick(function () {
                if (self.form.todo.users && self.form.todo.users.length === 0) {
                    self.form.todo.users.push({id: self.state.user.id, name: self.state.user.name});
                }
            });
        },

        setSelectedTenant (tenant) {
            this.selectedTenant = tenant;

            if (this.selectedTenant) {
                this.$set(this.form.ticket_details, "tenant_id", this.selectedTenant.id);
            } else {
                this.$set(this.form.ticket_details, "tenant_id", null);
                this.$set(this.form.ticket_notification_settings, "tenant_email_communication", null);
                this.$set(this.form.ticket_notification_settings, "tenant_sms_communication", null);
                if (this.form.errors.has("ticket_details.tenant_id")) {
                    this.form.errors.forget("ticket_details.tenant_id");
                }
            }
        },

        setSelectedLandlord (landlord) {
            this.selectedLandlord = landlord;

            if (this.selectedLandlord) {
                this.$set(this.form.ticket_details, "landlord_id", this.selectedLandlord.id);
            } else {
                this.$set(this.form.ticket_details, "landlord_id", null);
                this.$set(this.form.ticket_notification_settings, "landlord_email_communication", null);
                this.$set(this.form.ticket_notification_settings, "landlord_sms_communication", null);
                if (this.form.errors.has("ticket_details.landlord_id")) {
                    this.form.errors.forget("ticket_details.landlord_id");
                }
            }
        },

        setSelectedContactGroup (contactGroup) {
            this.selectedContactGroup = contactGroup;
            if (this.selectedContactGroup) {
                this.form.ticket_details.contact_group_id = this.selectedContactGroup.id;
                if (this.selectedContactGroup.contacts) {
                    this.setSelectedTenant(contactGroup.contacts.filter(contact => contact.pivot.role === "tenant" && contact.pivot.primary)?.[0]);
                }
            } else {
                this.$set(this.form.ticket_details, "contact_group_id", null);
                this.setSelectedTenant(this.resolvePrimaryTenant());
            }
        },

        setAssignedUser (user) {
            this.assignedToUser = user;
            if (this.assignedToUser) {
                this.form.ticket_details.assigned_to = this.assignedToUser.id;
                if (this.form.todo.users && this.form.todo.users.findIndex(u => u.id === this.assignedToUser.id) === -1) {
                    this.form.todo.users.push(this.assignedToUser);
                }
            } else {
                this.form.ticket_details.assigned_to = null;
            }

            if (this.form.errors.has("ticket_details.assigned_to")) {
                this.form.errors.forget("ticket_details.assigned_to");
            }
        },

        createTicket () {
            this.creating = true;

            if (this.form.service_providers && this.form.service_providers.length > 0) {
                this.form.service_providers.forEach(sp => {
                    if (sp.communication_channels) {
                        sp.communication_channels.forEach(channel => {
                            if (channel.saved_reply) {
                                if (channel.saved_reply.body) {
                                    channel.saved_reply.custom_body = channel.saved_reply.body;
                                }
                                if (channel.saved_reply.id) {
                                    channel.saved_reply.saved_reply_id = channel.saved_reply.id;
                                }
                            }
                        });
                    }
                });
            }

            App.postMultipartForm("/tickets/create", this.form)
            .then((response) => {
                if (this.hasFeature("release_ticket_backend_rework")) {
                    toastr.success("Ticket creation added to background tasks");
                } else {
                    toastr.success("Ticket Created!");
                }

                Bus.$emit("ticket-created", response.id);

                if (this.asset) {
                    this.$emit("navigate", response);
                }
                if (this.maintenanceRequest) {
                    this.$emit("created-from-maintenance-request", response);
                }
                if (this.inspection) {
                    this.$emit("created-from-inspection", {ticket: response, selectedChecklistItems: this.selectedChecklistItems});
                }

                this.refreshModal();
                this.$emit("close-modal");
                this.creating = false;
            })
            .catch(errors => {
                this.creating = false;
                toastr.error(errors.message);
                this.stepToError(errors);
            });
        },

        stepToError (errors) {
            let errorKey = Object.keys(errors.errors).toString();
            switch (true) {
                case errorKey.startsWith("ticket_details"):
                    this.goToSpecificSection(1);
                    break;
                case errorKey.startsWith("ticket_details.description"):
                    this.goToSpecificSection(2);
                    break;
                case errorKey.startsWith("issues"):
                    this.goToSpecificSection(2);
                    break;
                case errorKey.startsWith("task_settings"):
                    // Handle specific no issues when generating pdf case
                    errorKey.startsWith("task_settings.generate_pdf") ? this.goToSpecificSection(2) : this.goToSpecificSection(3);
                    break;
                case errorKey.startsWith("ticket_notification_settings"):
                    this.$refs.communicationSection.showTenantAndLandlordNotifications = true;
                    this.goToSpecificSection(4);
                    break;
                case errorKey.startsWith("service_providers"):
                    this.goToSpecificSection(4);
                    break;
            }
        },

        toggleCloseButton () {
            this.disableCloseButton = !this.disableCloseButton;
        },

        maintenanceRequestChanged () {
            // Maintenance request watcher did not update on first click, so we use this function in the mounted() as well
            this.preSelectedScenarioName = "opening ticket from maintenance request";
            this.$nextTick(function () {
                this.refreshModal();
            });
        },
    },
};
</script>
